/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormControl
} from '@angular/forms';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceHelper
} from '@insurance/helpers/insurance.helper';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ApiFilterHelper
} from '@shared/helpers/api-filter.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';
import {
	delay,
	from,
	Observable,
	of,
	switchMap
} from 'rxjs';

/* eslint-enable max-len */

@Component({
	selector: 'commission-payment-transaction',
	templateUrl: './commission-payment-transaction.component.html'
})

/**
 * A component representing a wizard step to collect the commission payment
 * transaction details.
 *
 * @export
 * @class CommissionPaymentTransactionComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class CommissionPaymentTransactionComponent
	implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext> {

	/**
	 * Initializes an instance of the commission payment transaction component.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service used in this component.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public constructor(
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService,
		public siteLayoutService: SiteLayoutService,
		public insuranceService: InsuranceService) {
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get currentData(): any {
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets the reference field configuration.
	 * 
	 * @type {FormlyFieldConfig}
	 * The reference field configuration.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get referenceFieldConfig(): FormlyFieldConfig {
		return this.dynamicFormlyLayout[1];
	}

	/**
	 * Gets the commission transaction field configuration.
	 * 
	 * @type {FormlyFieldConfig}
	 * The commission transaction field configuration.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get commissionTransactionFieldConfig(): FormlyFieldConfig {
		return this.dynamicFormlyLayout[3];
	}

	/**
	 * Gets the amount field configuration.
	 * 
	 * @type {FormlyFieldConfig}
	 * The amount field configuration.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public get amountFieldConfig(): FormlyFieldConfig {
		return this.dynamicFormlyLayout[4];
	}

	/**
	 * Gets or sets the transaction entity instance that
	 * is associated with the commission payment.
	 *
	 * @type {IEntityInstance}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public transaction: IEntityInstance;
	
	/**
	 * Gets or sets the payment association settings retrieved from
	 * the product associated to the transaction.
	 * 
	 * @type {any}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public paymentAssociationSettings: any;

	/**
	 * Gets or sets the list of commission transactions
	 * associated to the current transaction.
	 *
	 * @type {any[]}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public commissionTransactions: any[];

	/**
	 * Gets or sets the maximum amount allowed for the amount field.
	 * 
	 * @type {number}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private maximumAmount: number;

	/**
	 * Gets or sets the available entity types for transaction lookup.
	 * 
	 * @type {IEntityType[]}
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private availableEntityTypes: IEntityType[] = [];

	/**
	 * Implements the on initialization interface.
	 *
	 * @async
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		await this.loadTransactionEntityTypes();

		this.initializeLayout();

		this.context.source.addToNext(
			this.next.bind(this));

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the navigation to the next step.
	 *
	 * @async
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public next(): void
	{
		const noteOptions: IDropdownOption[] =
			<IDropdownOption[]>
				this.commissionTransactionFieldConfig.props.options;
		const referenceOptions: IDropdownOption[] =
			<IDropdownOption[]>
				this.referenceFieldConfig.props.options;

		this.context.source.addOrUpdateStepData(
			{
				note: noteOptions
					?.find(
						(item: any) =>
							item.value == this.currentData.commissionTransactionId)
					?.label || AppConstants.empty,
				reference: {
					name: this.paymentAssociationSettings?.label || 'Reference',
					id: this.currentData.referenceId,
					description: referenceOptions
						?.find(
							(item: any) =>
								item.value == this.currentData.referenceId)
						?.label || AppConstants.empty
				}
			});
	}

	/**
	 * Loads the available transaction entity types for lookup.
	 *
	 * @async
	 * @return {Promise<void>}
	 * The promise indicating the load completion.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async loadTransactionEntityTypes(): Promise<void>
	{
		const group: string = 'Group';

		this.availableEntityTypes =
			await this.entityTypeApiService
				.query(
					ApiFilterHelper.getWildcardFilter(
						group,
						InsuranceConstants
							.insuranceEntityTypeGroups
							.policyTermTransactionWildcard),
					group,
					null,
					AppConstants.dataLimits.large);
	}

	/**
	 * Validates the transaction number control.
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @return {boolean}
	 * The transaction number validity
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public transactionNumberValidator(
		control: AbstractControl): Observable<boolean>
	{
		if (AnyHelper.isNullOrWhitespace(control.value))
		{
			return of(false);
		}

		return of(control.value)
			.pipe(
				delay(
					AppConstants
						.time
						.threeQuarterSecond),
				switchMap((value: any) =>
					from(this.validateTransactionNumber(value))
				));
	}

	/**
	 * Validates the selected commission transaction.
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @return {boolean}
	 * The commission transaction validity.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public commissionTransactionValidator(
		control: AbstractControl): boolean
	{
		if (AnyHelper.isNullOrWhitespace(control.value))
		{
			return false;
		}
		
		const commissionAmount: number =
			this.commissionTransactions
				?.find(
					(item: any) =>
						item.id == control.value)
				?.data
				?.amount;

		return !AnyHelper.isNull(commissionAmount)
			&& commissionAmount > 0;
	}

	/**
	 * Validates the provided transaction number by checking
	 * if it exists and is associated to the current agency.
	 *
	 * @async
	 * @param value
	 * The transaction number value.
	 * @returns {Promise<boolean>}
	 * True if the transaction number is valid, otherwise false.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async validateTransactionNumber(
		value: string): Promise<boolean>
	{
		this.transaction = null;
		this.paymentAssociationSettings = null;

		for (const entityType of this.availableEntityTypes)
		{
			this.entityInstanceApiService.entityInstanceTypeGroup =
				entityType.group;

			this.transaction =
				await this.entityInstanceApiService
					.getSingleQueryResult(
						`${InsuranceConstants.commonProperties.transactionNumber} `
							+ `eq '${value}'`,
						`${AppConstants.commonProperties.id} `
							+ AppConstants.sortDirections.descending,
						true);

			if (!AnyHelper.isNull(this.transaction)
				&& (await this.insuranceService
					.isTransactionChildOfAgency(
						this.transaction,
						this.currentData.id)))
			{
				const product: IEntityInstance =
					await this.insuranceService
						.getProductByTransaction(
							this.transaction);

				this.paymentAssociationSettings =
					product?.data?.billing?.paymentAssociation;

				this.context.source.addOrUpdateStepData(
					{
						policyNumber:
							this.transaction
								.data
								.policyNumber,
						transactionId:
							this.transaction.id
					});

				setTimeout(() => {
					this.resetFields();
				}, AppConstants.time.oneHundredMilliseconds);

				return true;
			}
		}

		this.clearTransactionData();

		return false;
	}

	/**
	 * Clears the transaction related data from the current step
	 * and the wizard context.
	 * 
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public clearTransactionData(): void
	{
		this.transaction = null;
		this.paymentAssociationSettings = null;

		this.context.source.addOrUpdateStepData(
			{
				policyNumber: AppConstants.empty,
				transactionId: AppConstants.empty
			});

		setTimeout(() => {
			this.resetFields();
		}, AppConstants.time.oneHundredMilliseconds);
	}

	/**
	 * Resets the reference and commission transaction fields.
	 * 
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public resetFields(): void
	{
		this.referenceFieldConfig
			.formControl
			.setValue(null);
		this.referenceFieldConfig
			.formControl
			.updateValueAndValidity();

		if (this.referenceFieldConfig
			.props
			.initializeDataOptions)
		{
			this.referenceFieldConfig
				.props
				.initializeDataOptions();
		}

		this.commissionTransactionFieldConfig
			.formControl
			.setValue(null);
		this.commissionTransactionFieldConfig
			.formControl
			.updateValueAndValidity();

		if (this.commissionTransactionFieldConfig
			.props
			.initializeDataOptions)
		{
			this.commissionTransactionFieldConfig
				.props
				.initializeDataOptions();
		}
	}

	/**
	 * Loads the reference options for the reference field.
	 *
	 * @async
	 * @return {Promise<any[]>}
	 * The promise containing the list of reference options.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async loadReferenceOptions(): Promise<any[]>
	{
		return InsuranceHelper
			.loadPaymentReferenceOptions(
				this.transaction,
				this.paymentAssociationSettings);
	}

	/**
	 * Loads the commission transactions associated to the current transaction.
	 *
	 * @async
	 * @return {Promise<any[]>}
	 * The promise containing the list of commission transactions.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async loadCommissionTransactions(): Promise<any[]>
	{
		if (AnyHelper.isNull(this.transaction))
		{
			this.commissionTransactions = [];
			return [];
		}

		this.commissionTransactions =
			await this.insuranceService
				.getPayableCommissionsByTransactionId(
					this.currentData.id,
					this.transaction.id);

		return this.commissionTransactions;
	}

	/**
	 * Handles the transaction number field change event.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public onTransactionNumberChange(
		field: FormlyFieldConfig): void
	{
		if (AnyHelper.isNullOrWhitespace(
			field.formControl.value))
		{
			this.clearTransactionData();
		}
	}

	/**
	 * Handles the commission transaction field change event.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public onCommissionTransactionChange(
		field: FormlyFieldConfig): void
	{
		this.setMaximumAmount(
			field.formControl.value);
		this.amountFieldConfig
			.formControl
			.updateValueAndValidity();
	}

	/**
	 * Handles the Reference field change event.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public onReferenceChange(
		field: FormlyFieldConfig): void
	{
		if (AnyHelper.isNullOrWhitespace(
			field.formControl.value)
			|| AnyHelper.isNull(
				this.paymentAssociationSettings
					?.defaultAmountPath))
		{
			this.amountFieldConfig
				.formControl
				.setValue(null);

			return;
		}

		const items: any[] =
			this.getTransactionReferenceItems();
		const item: any =
			items
				?.find(
					(item: any) =>
						item.resourceIdentifier == this.currentData.referenceId);

		if (AnyHelper.isNull(item))
		{
			this.amountFieldConfig
				.formControl
				.setValue(null);

			return;
		}

		const amount: number =
			ObjectHelper
				.getPropertyByJsonPath(
					item,
					this.paymentAssociationSettings.defaultAmountPath);

		this.amountFieldConfig
			.formControl
			.setValue(
				amount);
	}

	/**
	 * Handles the validity changed event for the dynamic formly component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @async
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		this.context.source.validStepChanged(
			isValid 
				&& !AnyHelper.isNull(
					this.currentData.commissionTransactionId));
	}

	/**
	 * Gets the transaction reference items from the current transaction.
	 * 
	 * @return {any[]}
	 * The list of transaction reference items.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private getTransactionReferenceItems(): any[]
	{
		return InsuranceHelper
			.getPaymentReferenceItems(
				this.transaction,
				this.paymentAssociationSettings);
	}

	/**
	 * Sets the maximum amount allowed for the amount field.
	 * 
	 * @param {number} commissionTransactionId
	 * The commission transaction ID.
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private setMaximumAmount(
		commissionTransactionId: number): void
	{
		if (AnyHelper.isNullOrWhitespace(
			commissionTransactionId))
		{
			this.maximumAmount = null;
			return;
		}

		const commissionAmount: number =
			this.commissionTransactions
				?.find(
					(item: any) =>
						item.id == commissionTransactionId)
				?.data
				?.amount;

		if (AnyHelper.isNull(commissionAmount)
			|| commissionAmount <= 0)
		{
			this.maximumAmount = null;
			return;
		}

		this.maximumAmount =
			commissionAmount;
	}

	/**
	 * Creates the dynamic formly layout.
	 *
	 * @memberof CommissionPaymentTransactionComponent
	 */
	private initializeLayout(): void 
	{
		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'transactionNumber',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						type: 'string',
						label: 'Transaction',
						required: true,
						tooltip:
							'Transaction Id can be found in the Transaction '
							+ 'table displayed in the Policy Term Summary '
							+ 'page',
						change: this.onTransactionNumberChange.bind(this)
					},
					asyncValidators: {
						transactionNumberValidator: {
							expression: (
								control: UntypedFormControl,
								_: any) =>
									this.transactionNumberValidator(control),
							message:
								'This transaction number does not exist.'
						}
					}
				},
				{
					key: 'referenceId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Reference',
						placeholder: 'Select an Option',
						required: false,
						showClear: true,
						dataPromise:
							this.loadReferenceOptions.bind(this),
						labelTemplate: '${item.description}',
						valueTemplate: '${item.id}',
						change: this.onReferenceChange.bind(this)
					},
					expressions: {
						hide: () =>
							AnyHelper.isNullOrWhitespace(
								this.paymentAssociationSettings?.path),
						'props.label': () =>
							this.paymentAssociationSettings?.label || 'Reference'
					}
				},
				{
					wrappers: [
						"custom-empty-wrapper"
					],
					expressions: {
						hide: () =>
							!AnyHelper.isNullOrWhitespace(
								this.paymentAssociationSettings?.path)
					}
				},
				{
					key: 'commissionTransactionId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Notes',
						required: true,
						placeholder: 'Select an Option',
						showClear: true,
						dataPromise:
							this.loadCommissionTransactions.bind(this),
						labelFunction:
							'return function (item) { return item.data.note + \' - \' + '
								+ 'this.source.resolver.resolveStatic('
								+ '"StringHelper", "format", [ item.data.amount, "currency" ]); }',
						valueTemplate: '${item.id}',
						tooltip:
							'Select the commission transaction to pay. Only commission transactions '
								+ 'that have not been paid before or that are not part of an active '
								+ 'Commission Disbursement will be listed.',
						change: this.onCommissionTransactionChange.bind(this),
						disableAutoSetValue: true
					},
					validators: {
						commissionTransactionValidator: {
							expression: (control: UntypedFormControl) =>
								this.commissionTransactionValidator(control),
							message:
								'Only commission transactions with an amount '
									+ 'greater than $0 can be selected.'
						}
					}
				},
				{
					key: 'amount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Amount',
						useCurrency: true,
						required: true
					},
					validators: {
						greaterThanZero: {
							expression: (control: UntypedFormControl) =>
								control.value > 0,
							message: 'Amount must be greater than 0.'
						},
						maxAmount: {
							expression: (control: UntypedFormControl) =>
								AnyHelper.isNull(this.maximumAmount)
									|| control.value <= this.maximumAmount,
							message:
								'Amount cannot exceed the commission transaction amount.'
						}
					}
				},
				{
					key: 'transactionDate',
					type: FormlyConstants.customControls.customCalendar,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Bank Transaction Date',
						required: true
					}
				}
			];
	}
}